import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { DecimalPipe, NgClass } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { TimeWindow } from "../../../models/ui/timeWindow";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { FormsModule } from "@angular/forms";
import { SvgIconComponent } from "../../components/svg-icon/svg-icon.component";
import { createTimeoutSignal } from "../../utils/timeout.utils";
import { TimeoutComponent } from "../../components/timeout/timeout.component";
import { BotevoDesignTokens } from "../../../../assets/themes/botevoPreset";
import { Router } from "@angular/router";

@Component({
  standalone: true,
  selector: "app-expected-energy-loss",
  imports: [
    MatProgressSpinnerModule,
    NgClass,
    TranslateModule,
    ProgressSpinnerModule,
    FormsModule,
    SvgIconComponent,
    TimeoutComponent,
  ],
  templateUrl: "./expected-energy-loss.component.html",
  styleUrl: "./expected-energy-loss.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class ExpectedEnergyLossComponent {
  public botevoDesignTokens = BotevoDesignTokens;
  private decimalPipe = inject(DecimalPipe);
  private router = inject(Router);

  public compact = input<boolean>(false);
  public isCustomerYearly = input<boolean>(false);

  public energyLoss = input<number | null>();
  public expectedEnergyLossPerDay = input<number | null>();
  public energyCost = input<number | null>();

  @Output() changeTimeframeEvent = new EventEmitter<string>();

  public loading = computed(() => this.energyLoss() === null);
  public timeOut = createTimeoutSignal(this.loading);

  public displayedEnergyLoss = computed(() => {
    const energyLoss = this.energyLoss();
    if (energyLoss !== null && energyLoss !== undefined) {
      return this.formatValue(energyLoss);
    } else {
      return "";
    }
  });

  public displayedExpectedEnergyLoss = computed(() => {
    const expectedEnergyLossPerDay = this.expectedEnergyLossPerDay();
    const isCustomerYearly = this.isCustomerYearly();
    if (expectedEnergyLossPerDay !== null && expectedEnergyLossPerDay !== undefined) {
      const multiplyFactor = isCustomerYearly ? 365 : this.getMultiplyFactor(this.selectedTimeWindow());
      return this.formatValue(expectedEnergyLossPerDay * multiplyFactor);
    } else {
      return "";
    }
  });

  public displayedEnergyCosts = computed(() => {
    const energyCost = this.energyCost();
    if (energyCost !== null && energyCost !== undefined) {
      return this.formatValue(energyCost);
    } else {
      return "";
    }
  });

  public displayedEmissions = computed(() => {
    const energyLoss = this.energyLoss();
    if (energyLoss !== null && energyLoss !== undefined) {
      return this.calcEmissions(energyLoss);
    } else {
      return "";
    }
  });

  public selectedTimeWindow = signal<TimeWindow>("today");

  constructor() {
    effect(() => {
      const isCustomerYearly = this.isCustomerYearly();
      if (isCustomerYearly) {
        this.selectedTimeWindow.set("this_year");
      }
    });
  }

  public navigateToDetail() {
    const isCustomerYearly = this.isCustomerYearly();
    if (isCustomerYearly) {
      this.router.navigate(["dashboard/energyTool"]);
    }
  }

  /**
   * Given a time window (one of today, yesterday, this_week, this_month, this_year)
   * returns the number of days in that time window.
   * @param timeWindow The time window for which to calculate the number of days.
   * @returns The number of days in the given time window.
   */
  private getMultiplyFactor(timeWindow: TimeWindow) {
    switch (timeWindow) {
      case "today":
        return 1;
      case "yesterday":
        return 1;
      case "this_week":
        return 7;
      case "this_month":
        return 30;
      case "this_year":
        return 365;
      default:
        return 1;
    }
  }

  /**
   * Handles the change event for the time window select dropdown.
   * Updates the selected time window and emits an event with the new value.
   * @param changeEvent - The select element triggering the change event.
   */

  public onChangeTimeSelect(changeEvent: Event) {
    const timeWindow = (changeEvent.target as any).value;
    if (timeWindow) {
      this.selectedTimeWindow.set(timeWindow);
      this.changeTimeframeEvent.emit(timeWindow);
    }
  }
  /**
   * Calculate the emissions in kilograms from the given current value.
   * The factor 0.315 is used to convert amps to kilograms of CO2.
   * @param current The current value in amps to calculate emissions from.
   * @returns The calculated emissions in kilograms.
   */
  public calcEmissions(current: number) {
    return this.formatValue(current * 0.315);
  }
  /**
   * Format a given value to a string based on the user's filter selection.
   * @param value The value to format.
   * @returns The formatted string.
   */
  private formatValue(value: number) {
    const roundedValue = this.isCustomerYearly() ? Math.round(value) : Math.round(value * 100) / 100;
    return this.decimalPipe.transform(roundedValue, undefined, "de");
  }

  /**
   * Calculates the progress percentage based on the current value and the expected daily value.
   * Returns 0 if either value is null or undefined.
   * @param current - The current value.
   * @param expectedPerDay - The expected daily value.
   * @returns The progress percentage.
   */
  public getProgress(current: number | undefined | null, expectedPerDay: number | undefined | null) {
    if (current === undefined || expectedPerDay === undefined || current === null || expectedPerDay === null) return 0;
    const expected = expectedPerDay * this.getMultiplyFactor(this.selectedTimeWindow());
    return (current / expected) * 100;
  }
}
